import * as THREE from "three";
import { POLYGONS, RENDER_LAYERS, VARS } from "../Global/variables";
import { sceneManager } from "../SetUps/SceneManager";
import { pushUndoAction } from "../Global/UndoManager";
import { COLORS } from "../Global/materials";
import { applyBackFacePattern, createWireframe, getEdgeDirection, getPanelTiltLiftOffset, getPanelWireframe, getVertices } from "./Helpers";
import { checkPanelsOK, syncPanelSelectionVisuals } from "./PanelFunctions";
import { panelInSegment } from "./Placement";
import { getPanelImage } from "../../helpers/panelImageCache";
import { toast } from "../../lib/toast";

const CLEARANCE_M = 0.01;
const INCHES_PER_METER = 39.37;
let previewActive = false;
let ghosts = [];
let preview = { segmentIndex: null, anchor: null, valid: false };

const setPasteNotice = (visible, valid = true) => {
  window.__setCanvasNotice?.((current) => {
    if (!visible) return current?.source === "panel-copy-paste" ? { ...current, visible: false, source: null } : current;
    return {
      visible: true,
      source: "panel-copy-paste",
      intent: valid ? "info" : "warning",
      title: "Panel paste mode",
      subtitle: valid
        ? "Move over a segment and click to paste. Press Esc to cancel."
        : "Move until every preview outline is yellow, then click to paste. Press Esc to cancel.",
      closeLabel: "Cancel panel paste",
      onClose: () => cancelPanelPastePreview(),
      actions: [],
    };
  });
};

const getFrame = (segmentIndex) => {
  const segment = POLYGONS?.[segmentIndex];
  if (!segment?.shape) return null;
  // Raycast hits, copied panel positions, and rendered roof geometry are all
  // world-space. Build the placement plane from those same world-space roof
  // vertices instead of the raw geometry attributes used by createSegmentPlane.
  segment.shape.updateMatrixWorld?.(true);
  const vertices = getVertices(segment.shape);
  if (vertices.length < 3) return null;
  const plane = new THREE.Plane().setFromCoplanarPoints(vertices[0], vertices[1], vertices[2]);
  const normal = plane.normal.clone().normalize();
  if (normal.z < 0) { normal.negate(); plane.negate(); }
  const uAxis = getEdgeDirection(segment, normal).normalize();
  const vAxis = new THREE.Vector3().crossVectors(normal, uAxis).normalize();
  return { plane, normal, uAxis, vAxis };
};

const disposePanel = (panel) => {
  panel?.traverse?.((child) => {
    child.geometry?.dispose?.();
    if (Array.isArray(child.material)) child.material.forEach((m) => m?.dispose?.());
    else child.material?.dispose?.();
  });
};

const removeGhosts = () => {
  ghosts.forEach((panel) => { sceneManager.scene.remove(panel); disposePanel(panel); });
  ghosts = [];
  preview = { segmentIndex: null, anchor: null, valid: false };
};

const findPointerSegment = () => {
  const ndc = VARS.mouseNDC;
  if (!ndc || !sceneManager?.camera) return null;
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(ndc, sceneManager.camera);
  let nearest = null;
  POLYGONS.forEach((segment, segmentIndex) => {
    const hit = segment?.shape ? raycaster.intersectObject(segment.shape, false)[0] : null;
    if (hit && (!nearest || hit.distance < nearest.distance)) nearest = { segmentIndex, point: hit.point.clone(), distance: hit.distance };
  });
  return nearest;
};

const buildMaterial = (snapshot, ghost) => {
  const material = snapshot.material?.clone?.() || new THREE.MeshStandardMaterial({ roughness: 0.8, side: THREE.DoubleSide });
  // Match PanelManager's material for both the live ghost and the committed
  // panel. Otherwise a pasted panel changes its depth behavior at click time
  // and can be drawn beneath the selected segment's translucent fill.
  material.transparent = true;
  material.opacity = ghost ? 0.58 : 1;
  // Manual panels are intentionally drawn over roof fills and highlights.
  // Keep that policy after paste as well as during its preview.
  material.depthTest = false;
  material.depthWrite = false;
  const image = getPanelImage(snapshot.moduleId);
  if (image && !material.map) new THREE.TextureLoader().load(image, (texture) => {
    texture.wrapS = THREE.RepeatWrapping; texture.wrapT = THREE.RepeatWrapping;
    texture.rotation = snapshot.orientation === "Landscape" ? Math.PI / 2 : 0;
    material.map = texture; material.needsUpdate = true;
  });
  applyBackFacePattern(material);
  return material;
};

// The copied quaternion may retain a pitch different from the destination
// segment. Calculate the centre lift from the rotated panel footprint itself,
// rather than from UI tilt alone, so its lowest edge always stays above the
// destination roof plane.
const getSurfaceSafeCenterOffset = (snapshot, surfaceNormal) => {
  const halfWidth = Number(snapshot.widthM || 0) / 2;
  const halfHeight = Number(snapshot.heightM || 0) / 2;
  const quaternion = snapshot.quaternion;
  if (!(halfWidth > 0) || !(halfHeight > 0) || !quaternion) {
    return Number(snapshot.baseOffsetM || CLEARANCE_M);
  }

  const lowestProjection = [
    new THREE.Vector3(-halfWidth, -halfHeight, 0),
    new THREE.Vector3(halfWidth, -halfHeight, 0),
    new THREE.Vector3(halfWidth, halfHeight, 0),
    new THREE.Vector3(-halfWidth, halfHeight, 0),
  ].reduce((lowest, corner) => Math.min(
    lowest,
    corner.applyQuaternion(quaternion).dot(surfaceNormal)
  ), Infinity);

  return Number(snapshot.baseOffsetM || CLEARANCE_M) + Math.max(0, -lowestProjection);
};

const ensurePanelClearanceAboveSurface = (panel, frame, clearanceM) => {
  panel.updateMatrixWorld?.(true);
  const lowestDistance = getVertices(panel).reduce(
    (lowest, vertex) => Math.min(lowest, frame.plane.distanceToPoint(vertex)),
    Infinity
  );
  const correction = Number(clearanceM || CLEARANCE_M) - lowestDistance;
  if (Number.isFinite(correction) && correction > 1e-6) {
    panel.position.add(frame.normal.clone().multiplyScalar(correction));
    panel.updateMatrixWorld?.(true);
  }
};

const createPanel = (snapshot, segmentIndex, basePoint, ghost = false) => {
  const frame = getFrame(segmentIndex);
  if (!frame || !(snapshot.widthM > 0) || !(snapshot.heightM > 0)) return null;
  const panel = new THREE.Mesh(new THREE.PlaneGeometry(snapshot.widthM, snapshot.heightM), buildMaterial(snapshot, ghost));
  // Preserve the source runtime orientation. Unlike the sister codebase, this
  // designer keeps a panel tied to its parent segment's pitch after copying.
  const centerOffset = getSurfaceSafeCenterOffset(snapshot, frame.normal);
  panel.name = "panel";
  panel.panelType = snapshot.orientation === "Landscape" ? "landscape" : "potrait";
  panel.renderOrder = RENDER_LAYERS.PANEL;
  panel.frustumCulled = false;
  panel.quaternion.copy(snapshot.quaternion);
  panel.position.copy(basePoint.clone().add(frame.normal.multiplyScalar(centerOffset)));
  ensurePanelClearanceAboveSurface(panel, frame, snapshot.baseOffsetM);
  panel.userData = { panelTransform: { tilt: snapshot.tilt, rotation: snapshot.rotation, azimuth: snapshot.azimuth, segmentIndex }, __isPanelPasteGhost: ghost };
  createWireframe(panel);
  const wireframe = getPanelWireframe(panel);
  wireframe?.material?.setValues?.({
    transparent: true,
    opacity: 1,
    color: COLORS.yellow,
    // Keep the preview outline visible over the same roof overlays as the
    // preview face. This matches PanelManager's manual-placement wireframe.
    depthTest: false,
    depthWrite: false,
  });
  if (ghost) panel.traverse((child) => { child.raycast = () => {}; });
  return panel;
};

const clearSourceSelection = (selected) => {
  const segment = POLYGONS?.[selected?.segmentIndex];
  (selected?.panelIndex || []).forEach((index) => getPanelWireframe(segment?.panels?.[index]?.panel)?.material?.setValues?.({ opacity: 0 }));
};

export function copySelectedPanels(selected, uiPolygons) {
  const segmentIndex = selected?.segmentIndex;
  const indices = [...new Set(selected?.panelIndex || [])].filter(Number.isInteger).sort((a, b) => a - b);
  const frame = getFrame(segmentIndex);
  if (!frame || !indices.length) return false;
  const basePoints = [];
  const snapshots = indices.map((index) => {
    const record = POLYGONS?.[segmentIndex]?.panels?.[index];
    const mesh = record?.panel;
    if (!mesh?.geometry) return null;
    const ui = uiPolygons?.[segmentIndex]?.panels?.[index] || {};
    const transform = mesh.userData?.panelTransform || {};
    const heightM = Number(mesh.geometry.parameters.height || 0);
    const distance = frame.plane.distanceToPoint(mesh.position);
    const baseOffsetM = Math.max(CLEARANCE_M, distance - getPanelTiltLiftOffset(heightM, transform.tilt ?? ui.tilt ?? 0));
    const base = mesh.position.clone().add(frame.normal.clone().multiplyScalar(-distance));
    basePoints.push(base);
    return { moduleId: record.id ?? ui.id, orientation: ui.orientation ?? (mesh.panelType === "landscape" ? "Landscape" : "Portrait"), tilt: Number(transform.tilt ?? ui.tilt ?? 0), rotation: Number(transform.rotation ?? ui.rotation ?? 0), azimuth: Number(transform.azimuth ?? ui.azimuth ?? 0), quaternion: mesh.quaternion.clone(), height: Number(ui.height ?? 3), widthM: Number(mesh.geometry.parameters.width), heightM, baseOffsetM, material: mesh.material, uiPanel: { ...ui } };
  }).filter(Boolean);
  if (!snapshots.length) return false;
  const anchor = basePoints.reduce((sum, point) => sum.add(point), new THREE.Vector3()).multiplyScalar(1 / basePoints.length);
  snapshots.forEach((snapshot, index) => { const offset = basePoints[index].clone().sub(anchor); snapshot.offsetU = offset.dot(frame.uAxis); snapshot.offsetV = offset.dot(frame.vAxis); });
  VARS.copiedPanel = { sourceSegmentIndex: segmentIndex, anchor, snapshots };
  VARS.copiedObstacle = null; VARS.copiedSegment = null; VARS.clipboardType = "panel";
  clearSourceSelection(selected);
  toast.success(snapshots.length > 1 ? "Panels copied" : "Panel copied", { duration: 1200 });
  startPanelPastePreview();
  return true;
}

const updatePreview = () => {
  if (!previewActive || !VARS.copiedPanel) return;
  const target = findPointerSegment();
  if (!target) { removeGhosts(); setPasteNotice(true, false); return; }
  const payload = VARS.copiedPanel;
  if (preview.segmentIndex !== target.segmentIndex || ghosts.length !== payload.snapshots.length) {
    removeGhosts();
    const frame = getFrame(target.segmentIndex);
    ghosts = payload.snapshots.map((snapshot) => createPanel(snapshot, target.segmentIndex, target.point.clone().add(frame.uAxis.clone().multiplyScalar(snapshot.offsetU)).add(frame.vAxis.clone().multiplyScalar(snapshot.offsetV)), true)).filter(Boolean);
    ghosts.forEach((panel) => sceneManager.scene.add(panel));
  }
  const frame = getFrame(target.segmentIndex);
  ghosts.forEach((ghost, index) => {
    const snapshot = payload.snapshots[index];
    const base = target.point.clone().add(frame.uAxis.clone().multiplyScalar(snapshot.offsetU)).add(frame.vAxis.clone().multiplyScalar(snapshot.offsetV));
    ghost.position.copy(base.add(frame.normal.clone().multiplyScalar(
      getSurfaceSafeCenterOffset(snapshot, frame.normal)
    )));
    ensurePanelClearanceAboveSurface(ghost, frame, snapshot.baseOffsetM);
  });
  const valid = ghosts.length === payload.snapshots.length && ghosts.every((panel) => panelInSegment(panel, target.segmentIndex));
  ghosts.forEach((panel) => getPanelWireframe(panel)?.material?.setValues?.({ color: valid ? COLORS.yellow : COLORS.red, opacity: 1 }));
  preview = { segmentIndex: target.segmentIndex, anchor: target.point.clone(), valid };
  setPasteNotice(true, valid);
};

export function startPanelPastePreview() { previewActive = true; setPasteNotice(true, true); updatePreview(); }
export function stopPanelPastePreview() { previewActive = false; removeGhosts(); setPasteNotice(false); }
export function isPanelPastePreviewActive() { return previewActive; }
export function cancelPanelPastePreview() {
  stopPanelPastePreview();
  VARS.copiedPanel = null;
  if (VARS.clipboardType === "panel") VARS.clipboardType = null;
}

export function pasteCopiedPanels({ setPolygons, setSelectedIndices, setSolarPanel, setPanelsOK, arePanelsOK }) {
  const payload = VARS.copiedPanel;
  if (!payload || !preview.valid || !Number.isInteger(preview.segmentIndex)) { toast.warning("Move the copied panels to a valid location first"); return false; }
  const targetSegmentIndex = preview.segmentIndex;
  const anchor = preview.anchor.clone();
  const frame = getFrame(targetSegmentIndex);
  const panels = payload.snapshots.map((snapshot) => createPanel(snapshot, targetSegmentIndex, anchor.clone().add(frame.uAxis.clone().multiplyScalar(snapshot.offsetU)).add(frame.vAxis.clone().multiplyScalar(snapshot.offsetV)))).filter(Boolean);
  if (panels.length !== payload.snapshots.length || !panels.every((panel) => panelInSegment(panel, targetSegmentIndex))) { panels.forEach(disposePanel); toast.warning("Panels can't be pasted at this location"); return false; }
  stopPanelPastePreview();
  const segment = POLYGONS[targetSegmentIndex];
  const entries = panels.map((panel, index) => {
    const snapshot = payload.snapshots[index]; sceneManager.scene.add(panel);
    const entry = { panel, area: panel.geometry.parameters.width * panel.geometry.parameters.height * 10.764, id: snapshot.moduleId };
    segment.panels.push(entry);
    return { index: segment.panels.length - 1, entry, ui: { ...snapshot.uiPanel, id: snapshot.moduleId, panel_id: panel.id, tilt: snapshot.tilt, rotation: snapshot.rotation, azimuth: panel.userData.panelTransform.azimuth, orientation: snapshot.orientation, height: snapshot.height, offset: snapshot.baseOffsetM * INCHES_PER_METER } };
  });
  const addUi = () => setPolygons((prev) => { const next = [...prev]; const target = { ...next[targetSegmentIndex] }; target.panels = [...(target.panels || []), ...entries.map((item) => item.ui)]; next[targetSegmentIndex] = target; return next; });
  addUi();
  const pastedIndices = entries.map((item) => item.index);
  setSelectedIndices({ segmentIndex: targetSegmentIndex, obstacleIndex: null, panelIndex: pastedIndices });
  syncPanelSelectionVisuals(targetSegmentIndex, pastedIndices);
  setSolarPanel?.(entries[0].ui); checkPanelsOK(targetSegmentIndex, setPanelsOK, arePanelsOK);
  pushUndoAction({
    label: entries.length > 1 ? "Paste panels" : "Paste panel",
    undo: () => {
      [...pastedIndices].sort((a, b) => b - a).forEach((index) => {
        const panel = segment.panels[index]?.panel;
        sceneManager.scene.remove(panel);
        segment.panels.splice(index, 1);
      });
      setPolygons((prev) => {
        const next = [...prev];
        const target = { ...next[targetSegmentIndex] };
        target.panels = (target.panels || []).filter((_, index) => !pastedIndices.includes(index));
        next[targetSegmentIndex] = target;
        return next;
      });
      setSelectedIndices({ segmentIndex: targetSegmentIndex, obstacleIndex: null, panelIndex: null });
      checkPanelsOK(targetSegmentIndex, setPanelsOK, arePanelsOK);
    },
    redo: () => {
      const nextIndices = [];
      entries.forEach((item) => {
        sceneManager.scene.add(item.entry.panel);
        segment.panels.push(item.entry);
        nextIndices.push(segment.panels.length - 1);
      });
      setPolygons((prev) => {
        const next = [...prev];
        const target = { ...next[targetSegmentIndex] };
        target.panels = [...(target.panels || []), ...entries.map((item) => item.ui)];
        next[targetSegmentIndex] = target;
        return next;
      });
      setSelectedIndices({ segmentIndex: targetSegmentIndex, obstacleIndex: null, panelIndex: nextIndices });
      syncPanelSelectionVisuals(targetSegmentIndex, nextIndices);
      checkPanelsOK(targetSegmentIndex, setPanelsOK, arePanelsOK);
    },
  });
  toast.success(entries.length > 1 ? "Panels pasted" : "Panel pasted");
  return true;
}

if (typeof window !== "undefined") {
  window.addEventListener("mousemove", () => updatePreview());
  window.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !previewActive) return;
    const target = event.target;
    if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable) return;
    event.preventDefault();
    cancelPanelPastePreview();
  }, true);
}
